import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { Loader2, Upload, FileJson, AlertCircle, CheckCircle2 } from "lucide-react";

interface ImportWorkflowDialogProps {
  open: boolean;
  onClose: () => void;
}

export function ImportWorkflowDialog({ open, onClose }: ImportWorkflowDialogProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<any>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.json')) {
      setError('Please select a JSON file');
      return;
    }

    setFile(selectedFile);
    setError(null);

    // Read and parse file
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const json = JSON.parse(event.target?.result as string);
        
        // Validate structure
        if (!json.workflow_key || !json.title || !json.category) {
          throw new Error('Invalid workflow format: missing required fields');
        }

        setPreview(json);
      } catch (err: any) {
        setError(err.message || 'Failed to parse JSON file');
        setPreview(null);
      }
    };
    reader.readAsText(selectedFile);
  }

  async function handleImport() {
    if (!preview) return;

    setImporting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Check if workflow_key already exists
      const { data: existing } = await supabase
        .from('workflows')
        .select('id')
        .eq('workflow_key', preview.workflow_key)
        .single();

      if (existing) {
        // Generate unique key
        preview.workflow_key = `${preview.workflow_key}-imported-${Date.now()}`;
        preview.title = `${preview.title} (Imported)`;
      }

      // Clean data for import
      const importData = {
        workflow_key: preview.workflow_key,
        title: preview.title,
        description: preview.description || '',
        category: preview.category,
        icon_name: preview.icon_name || 'file',
        estimated_time: preview.estimated_time || '',
        difficulty: preview.difficulty || 'beginner',
        tier_restrictions: preview.tier_restrictions || [],
        prerequisites: preview.prerequisites || [],
        phases: preview.phases || [],
        success_criteria: preview.success_criteria || [],
        guidance_notes: preview.guidance_notes || '',
        is_active: false, // Import as inactive by default
        sort_order: preview.sort_order || 0,
        version: 1,
        created_by: user.id,
        feature_flags: preview.feature_flags || {},
      };

      const { data: workflow, error: importError } = await supabase
        .from('workflows')
        .insert(importData)
        .select()
        .single();

      if (importError) throw importError;

      toast({
        title: "Workflow Imported",
        description: "Workflow has been imported successfully. Review and activate when ready.",
      });

      onClose();
      navigate(`/admin/templates/workflows/${workflow.id}/edit`);
    } catch (error: any) {
      console.error('Error importing workflow:', error);
      setError(error.message || 'Failed to import workflow');
      toast({
        title: "Import Failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setImporting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Import Workflow
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert>
            <FileJson className="h-4 w-4" />
            <AlertDescription>
              Import a workflow from a JSON export file. The workflow will be imported as inactive and you can review it before activating.
            </AlertDescription>
          </Alert>

          {/* File Upload */}
          <div className="space-y-2">
            <label
              htmlFor="workflow-file"
              className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted transition-colors"
            >
              <div className="flex flex-col items-center justify-center pt-5 pb-6">
                <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  {file ? file.name : 'Click to select workflow JSON file'}
                </p>
              </div>
              <input
                id="workflow-file"
                type="file"
                className="hidden"
                accept=".json"
                onChange={handleFileSelect}
              />
            </label>
          </div>

          {/* Error Display */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Preview */}
          {preview && (
            <Card className="p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">Preview</h3>
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              </div>

              <div className="space-y-2">
                <div>
                  <p className="text-sm text-muted-foreground">Title</p>
                  <p className="font-medium">{preview.title}</p>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">Key</p>
                  <p className="text-sm font-mono">{preview.workflow_key}</p>
                </div>

                <div className="flex gap-2 flex-wrap">
                  <Badge variant="outline">{preview.category}</Badge>
                  <Badge variant="outline">{preview.difficulty}</Badge>
                  <Badge variant="outline">
                    {Array.isArray(preview.phases) ? preview.phases.length : 0} phases
                  </Badge>
                </div>

                <div>
                  <p className="text-sm text-muted-foreground">Description</p>
                  <p className="text-sm">{preview.description || 'No description'}</p>
                </div>
              </div>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={importing}>
            Cancel
          </Button>
          <Button onClick={handleImport} disabled={!preview || importing}>
            {importing ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Import Workflow
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
